import styled from "@emotion/styled";
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import avatar from "src/assets/images/avatar.png";
import { DisplayDateFormat, i18n, mediaQuery, setFlowType, setProgressBarPercentage, setSoId, useAppDispatch,  } from 'src/common';
import { processQueryResult } from 'src/common/api/apollo';
import { useCheckoutHealthOfferDataQuery, useCheckoutHealthOffersQuery, useGetAdminQuery } from 'src/common/api/apollo/supplementary';
import { Card, Divider, Flex, Grid, Label, Loader, OfferSoCard, Text } from 'src/components';
import config from 'src/config';
import { colors } from 'src/theme';

const ImageStyle = styled.img({
  height: '54px',
  width: '54px',
  borderRadius: '50%',
  objectFit: 'cover'
})

const GridStyle = styled(Grid)({
  columns: '1fr',

  [mediaQuery('mobile')]: {
    columns: '1fr auto',
  },

  [mediaQuery('laptopL')]: {
    marginInline: '-64px'
  }
})

const SoPage = () => {
  const dispatch = useAppDispatch();
  const [offerId, setOfferId] = useState<string | undefined>(undefined);

  useEffect(() => {
    dispatch(setProgressBarPercentage(40));
    dispatch(setFlowType('agent'));
  }, []);

  const params = useParams<{ soId: string }>();
  const soId = params.soId as string;

  useEffect(() => {
    dispatch(setSoId(soId));
  }, [soId]);

  const getCheckoutHealthOfferQuery = useCheckoutHealthOffersQuery({
    variables: { soId },
    fetchPolicy: 'cache-first',
    onCompleted: (result: any) => {
      processQueryResult(result);
    }
  });

  const getAdmin = useGetAdminQuery({
    variables: { referenceId: soId }
  });

  const getCheckoutHealthOfferData = useCheckoutHealthOfferDataQuery({
    variables: { offerId: offerId || "" },
    fetchPolicy: 'cache-first',
    skip: !offerId,
    onCompleted: (result: any) => {
      processQueryResult(result);
    }
  });

  useEffect(() => {
    const healthOffers = getCheckoutHealthOfferQuery.data?.checkoutHealthOffers;
    if (healthOffers && healthOffers.length > 0) {
      const firstOfferId = healthOffers[0]?._id;
      setOfferId(firstOfferId);
    }
  }, [getCheckoutHealthOfferQuery.data?.checkoutHealthOffers]);

  if (getCheckoutHealthOfferQuery.loading || getAdmin.loading) return <Loader page />;
  if (getCheckoutHealthOfferQuery.error) return null;

  // Destrukturiranje podataka
  const { checkoutHealthOffers } = getCheckoutHealthOfferQuery.data || {};

  const assignedAdminByReference = getAdmin.data?.getAdmin;

  const { checkoutHealthOfferData } = getCheckoutHealthOfferData.data || {};

  if (!checkoutHealthOffers) return null;

  // Sortiranje podataka po cijeni
  const sortedOffers = checkoutHealthOffers.slice().sort((a: any, b: any) => a.total.generalTransfer - b.total.generalTransfer);

  return (
    <Flex vertical gap={24}>
      <GridStyle gap={'24px'}>
        <Flex vertical gap={12}>
          <Label type='small' color={colors.text4}>{i18n.t('pages.soPage.label')}</Label>
          <Text type='large' bold>{i18n.t('pages.soPage.title')}</Text>
        </Flex>
        <Flex gap={12}>
          <ImageStyle src={assignedAdminByReference?.avatar ? `${config.REACT_APP_KOMPARE_HOME}/${assignedAdminByReference.avatar}` : avatar} alt={'profilePicture'} />
          <Flex vertical gap={4}>
            <Text type='tiny' color={colors.text3} bold>{assignedAdminByReference?.name}</Text>
            <Text type='tiny' color={colors.text3}>{assignedAdminByReference?.phone}</Text>
            <Text type='tiny' color={colors.text3}>{'PON-PET 08-20h /SUB 10-14h'}</Text>
          </Flex>
        </Flex>
      </GridStyle>
      <Card padding='12px 24px' radius='8px' background={colors.success2}>
        {getCheckoutHealthOfferData.loading ? <Loader /> :
          <>
            { checkoutHealthOfferData?.insuredPerson?.firstName?.value && checkoutHealthOfferData?.insuredPerson?.lastName?.value && <Text type='tiny' color={colors.text3} bold>{`${i18n.t('pages.soPage.insuredPerson')}: ${checkoutHealthOfferData?.insuredPerson?.firstName?.value} ${checkoutHealthOfferData?.insuredPerson?.lastName?.value}`}</Text>}
            <Text type='tiny' color={colors.text3}>{`${i18n.t('pages.soPage.birthDate')}: ${dayjs(checkoutHealthOfferData?.insuredPerson?.birthDate?.value).format(DisplayDateFormat)}`}</Text>
            { checkoutHealthOfferData?.insuredPerson?.oib?.value && <Text type='tiny' color={colors.text3}>{`${i18n.t('pages.soPage.oib')}: ${checkoutHealthOfferData?.insuredPerson?.oib?.value}`}</Text> }
          </>}
      </Card>
      <Divider dashed />
      {sortedOffers?.map((offer: any) => (
        <OfferSoCard key={offer._id} offer={offer} />
      ))}
    </Flex>
  );
}

export default SoPage;
